import React from 'react';
import { AnyAction } from 'redux';
import { all, takeEvery, fork, put, select, delay, call, spawn } from 'redux-saga/effects';
import { notification } from 'antd';

import { store } from '../../redux/store';

import { setMaintenanceMode } from '../appConfig/actions';
import utilityActions from '../utility/actions';
import marksActions from '../marks/actions';
import { authActionTypes, authActionsInterfaces, authActions, authSelectors } from '.';
import { AuthState } from './interfaces';

import Auth from '../../services/auth';

import StorageManager from '../../helpers/storageManager';
import { isTabWithForm } from '../dynamicTabs/helpers';
import { NotificationTypes } from '../utility/types';
import { codes } from '../../settings/codes';

import { authServiceUrl, casLogoutUrl, roles } from '../../settings';
import Translation from '../../components/UI/Translation';
import { appConfigActions } from '../appConfig';

import { createBodyFromError, createBodyFromErrorWithText } from '../../helpers/notificationFromDatabase';

const { openConfirmCodeModal } = marksActions;
const { showNotification } = utilityActions;
const { executeErrorCode } = authActions;

export function* checkTokenWatcher(): Generator {
  yield takeEvery(authActionTypes.CHECK_TOKEN, checkToken);
}
/**
 * Inicjalizacja sprawdzenia ważności tokenu + akcje pomocnicze robione na starcie
 *
 * @export
 * @return {*}  {Generator}
 */
export function* checkToken(): Generator {
  yield put(appConfigActions.checkApiStatus());
  yield put(authActions.whoAmI());
  yield put(authActions.initAutoRefresh());
}

export function* initAutoRefreshWatcher(): Generator {
  yield takeEvery(authActionTypes.INIT_AUTO_REFRESH, initAutoRefresh);
}
/**
 * Inizjalizacja automatycznego odświeżenia tokenu w określonych warunkach
 *
 * @export
 * @return {*}  {Generator}
 */
export function* initAutoRefresh(): Generator {
  const minutes = 3;
  /* sprawdzenie czy istnieje zakładka posiadająca otwarty do edycji formularz */
  yield call(
    setInterval,
    () => {
      const { tabList } = store.getState().DynamicTabs;
      const existOpenForm = tabList.find((tab): any => isTabWithForm(tab) && !tab.form.readOnly);
      if (existOpenForm) {
        store.dispatch(authActions.whoAmIBackground());
      }
    },
    minutes * 1000 * 60,
  );
}

export function* whoIAmWatcher(): Generator {
  yield takeEvery(authActionTypes.WHO_AM_I_START, whoIAm);
}
/**
 * Zapytanie o dane użytkownika
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* whoIAm(action: authActionsInterfaces.WhoAmI): Generator {
  try {
    /* wysłanie zapytania o dane użytkownika */
    const response: any = yield call(Auth.whoIAm);
    if (response && response.user) {
      yield put(authActions.whoAmISuccess(response.user, response.token));
      yield put(appConfigActions.getNotificationListAdmin(response.user.id));
    }
  } catch (error) {
    if (error.errorCode === codes.EXPIRED_TOKEN) {
      yield put(executeErrorCode(error.errorCode, action, error));
    } else {
      yield put(authActions.whoAmIFail());
      /* w przypadku nieobsługiwanego błędu usuń zawartość storage managera oraz przekieruj do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      StorageManager.remove('TABS_CONFIG');
      yield put(authActions.redirectToAuthService());
    }
  }
}

export function* whoIAmBackgroundWatcher(): Generator {
  yield takeEvery(authActionTypes.WHO_AM_I_BACKGROUND_START, whoIAmBackground);
}
/**
 * Zapytanie o dane użytkownika obsługiwane w tle
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* whoIAmBackground(action: authActionsInterfaces.WhoAmIBackground): Generator {
  try {
    /* wysłanie zapytania o dane użytkownika */
    yield call(Auth.whoIAm);
  } catch (error) {
    if (error.errorCode === codes.EXPIRED_TOKEN) {
      yield put(executeErrorCode(error.errorCode, action, error));
    } else {
      yield put(authActions.whoAmIFail());
      /* w przypadku nieobsługiwanego błędu usuń zawartość storage managera oraz przekieruj do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      StorageManager.remove('TABS_CONFIG');
      yield put(authActions.redirectToAuthService());
    }
  }
}

export function* refreshTokenWatcher(): Generator {
  yield takeEvery(authActionTypes.REFRESH_TOKEN_START, refreshToken);
}
/**
 * odświeżenie tokenu
 *
 * @export
 * @return {*}  {Generator}
 */
export function* refreshToken(): Generator {
  try {
    const refreshToken = StorageManager.get('refresh_token');
    if (refreshToken) {
      const response = (yield call(Auth.refreshToken, refreshToken)) as authActionsInterfaces.Token;
      yield put(authActions.refreshTokenSuccess(response));
    } else {
      throw new Error('Missing refresh token');
    }
  } catch {
    // w razie błędu usuń token i refresh token z localStorage jeśli takowe w nim istnieją
    StorageManager.remove('id_token');
    StorageManager.remove('refresh_token');
    // kiedy jesteś na koncie 'zalogowany jako' redirect leci na 'normalne' konto, więc czyści zakładki
    const parent = (yield select(authSelectors.getParentUser)) as AppConfig.User | null;
    if (parent) {
      StorageManager.remove('TABS_CONFIG');
    }
    yield put(authActions.refreshTokenFail());
    // wywołaj akcję REDIRECT_TO_AUTH_SERVICE
    yield put(authActions.redirectToAuthService());
  }
}

export function* storeTokenWatcher(): Generator {
  yield takeEvery([authActionTypes.WHO_AM_I_SUCCESS, authActionTypes.REFRESH_TOKEN_SUCCESS], storeToken);
}
/**
 * zapisanie tokenów po udanym odświeżeniu
 *
 * @export
 * @return {*}  {Generator}
 */
export function* storeToken(action: authActionsInterfaces.RefreshTokenSuccess | authActionsInterfaces.WhoAmISuccess): Generator {
  const { token } = action.payload;
  yield spawn(StorageManager.set, 'id_token', token.token, true);
  yield spawn(StorageManager.set, 'refresh_token', token.refreshToken, true);

  if (action.type === authActionTypes.REFRESH_TOKEN_SUCCESS) {
    yield call(executeActionsFromActionAccumulator);
  }
}

export function* executeActionsFromActionAccumulator(): Generator {
  const actionAccumulator = (yield select(authSelectors.getActionAccumulator)) as AnyAction[];

  for (let i = 0; i < actionAccumulator.length; i++) {
    yield put(actionAccumulator[i]);
  }

  yield put(authActions.clearAccumulator());
}

export function* executeActionCodesWatcher(): Generator {
  yield takeEvery(authActionTypes.EXECUTE_ACTION_CODE, executeActionCodes);
}
/**
 * obsługa kodów akcji
 *
 * @export
 * @param {AnyAction} { payload }
 * @return {*}  {Generator}
 */
export function* executeActionCodes({ payload }: authActionsInterfaces.ExecuteActionCode): Generator {
  const code = Number(payload.code);
  switch (code) {
    case codes.SECOND_FA_SMS_STARTED:
      // Otwórz okno sms
      yield put(openConfirmCodeModal('sms'));
      break;
    case codes.SECOND_FA_EMAIL_STARTED:
      // Otwórz okno sms
      yield put(openConfirmCodeModal('email'));
      break;
    default:
      break;
  }
}

export function* executeErrorCodesWatcher(): Generator {
  yield takeEvery(authActionTypes.EXECUTE_ERROR_CODE, executeErrorCodes);
}
/**
 * obsługa kodów błędów
 *
 * @export
 * @param {AnyAction} { payload }
 * @return {*}  {Generator}
 */
export function* executeErrorCodes({ payload }: authActionsInterfaces.ExecuteErrorCode): Generator {
  const { code, error, failedAction } = payload;
  const errorCode = Number(code);
  console.log(error);
  const user = (yield select(authSelectors.getUser)) as AppConfig.User;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refreshingTokenInProgress = (yield select(
    authSelectors.getRefreshingTokenInProgress,
  )) as AuthState['refreshingTokenInProgress'];
  switch (errorCode) {
    case codes.SUBJECT_CARDS_SAVE_SID_ERROR_NO_EFEKTY_KIERUNKOWE:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SUBJECT_CARDS_SAVE_SID_ERROR_NO_EFEKTY_KIERUNKOWE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SUBJECT_CARDS_SAVE_SID_ERROR_NO_EFEKTY_KIERUNKOWE_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.ERROR_PROTOCOL_GENERATION_NO_STUDENTS_WITH_SIGNED_MARKS:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATION_ERROR_NO_STUDENTS_FOR_SUBJECT_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_GENERATION_ERROR_NO_STUDENTS_FOR_SUBJECT_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.ERROR_PROTOCOL_GENERATION_USER_NOT_AUTHORIZED:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATION_ERROR_USER_NOT_AUTHORIZED',
          bodyTranslationKey: createBodyFromError(error),
          duration: 15,
        }),
      );
      break;
    case codes.ERROR_PROTOCOL_GENERATION_OUT_OF_DATE:
    case codes.ERROR_PROTOCOL_GENERATION_IS_NOT_AVAILABLE_NEW:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATION_OUT_OF_DATE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_GENERATION_OUT_OF_DATE_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.ERROR_PROTOCOL_GENERATION_HAS_NOT_FINISHED:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATION_HAS_NOT_FINISHED_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_GENERATION_HAS_NOT_FINISHED_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.DATABASE_ERROR_PROTOKOLY_1:
    case codes.DATABASE_ERROR_PROTOKOLY_2:
    case codes.DATABASE_ERROR_PROTOKOLY_3:
    case codes.DATABASE_ERROR_PROTOKOLY_4:
    case codes.DATABASE_ERROR_PROTOKOLY_5:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_DATABASE_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_DATABASE_ERROR_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.DATABASE_ERROR_PROTOKOLY_6:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_DATABASE_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error),
          duration: 15,
        }),
      );
      break;
    case codes.GENERATE_ERROR_PROTOKOLY_1:
    case codes.GENERATE_ERROR_PROTOKOLY_2:
    case codes.GENERATE_ERROR_PROTOKOLY_3:
    case codes.GENERATE_ERROR_PROTOKOLY_4:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATE_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_GENERATE_ERROR_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.ERROR_PROTOCOL_GENERATION_NO_PROTOCOL_FOR_PARAMETERS:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'PROTOCOL_GENERATION_ERROR_NO_STUDENTS_FOR_SUBJECT_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'PROTOCOL_GENERATION_ERROR_NO_PROTOCOL_FOR_PARAMETERS_DESCRIPTION'),
          duration: 15,
        }),
      );
      break;
    case codes.COPY_FINAL_MARKS_ERROR_ADDING_GRADE_TO_DB:
    case codes.DATABASE_ERROR:
    case codes.DATABASE_ERROR_2:
      //wyświetl informację użytkownikowi
      notification.error({
        top: 100,
        message: <Translation id='DATABASE_ERROR_TITLE' />,
        description: (
          <>
            <Translation id='DATABASE_ERROR_BODY_1' />
            {error && error.hash ? (
              <p>
                <Translation id='DATABASE_ERROR_BODY_2' />
                {error.hash}
              </p>
            ) : null}
          </>
        ),
        duration: 0,
      });
      break;
    case codes.MARKS_ERROR_WITH_MESSAGE:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'UPDATE_MARK_CUSTOM_MESSAGE_TITLE',
          bodyTranslationKey: createBodyFromErrorWithText(error),
          duration: 15,
        }),
      );
      break;
    case codes.SIGN_MARK_INVALID_MARK_IMPORTANCE:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SIGN_MARK_INVALID_MARK_IMPORTANCE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SIGN_MARK_INVALID_MARK_IMPORTANCE_BODY'),
        }),
      );
      break;
    case codes.SIGN_MARK_WRONG_IP_RANGE:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SIGN_MARK_WRONG_IP_RANGE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SIGN_MARK_WRONG_IP_RANGE_BODY'),
        }),
      );
      break;
    case codes.WRONG_EMAIL_CODE:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'WRONG_CODE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'WRONG_EMAIL_CODE_BODY'),
        }),
      );
      break;
    case codes.WRONG_SMS_CODE:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'WRONG_CODE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'WRONG_SMS_CODE_BODY'),
        }),
      );
      break;
    case codes.SMS_CODE_EXPIRED:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SMS_CODE_EXPIRED_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SMS_CODE_EXPIRED_BODY'),
        }),
      );
      break;
    case codes.SAVE_IN_SID_UPDATE_ERROR_1:
    case codes.SAVE_IN_SID_UPDATE_ERROR_3:
    case codes.SAVE_IN_SID_UPDATE_ERROR_4:
    case codes.SAVE_IN_SID_UPDATE_ERROR_5:
    case codes.SAVE_IN_SID_UPDATE_ERROR_6:
    case codes.SAVE_IN_SID_UPDATE_ERROR_7:
    case codes.SAVE_IN_SID_UPDATE_ERROR_8:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SAVE_IN_SID_UPDATE_CANCELLED',
          bodyTranslationKey: createBodyFromErrorWithText(error),
        }),
      );
      break;
    case codes.SAVE_IN_SID_UPDATE_ERROR_2:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'CANNOT_GET_SUBJECT_INFO',
          bodyTranslationKey: createBodyFromError(error),
        }),
      );
      break;
    case codes.TEACHER_SESSION_ACTIVE_ACCESS_ERROR:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SUPERTEACHER_MODAL_RESOURCES_ACCESS_ERROR_SESSION_TEACHER_ACTIVE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SUPERTEACHER_MODAL_RESOURCES_ACCESS_ERROR_SESSION_TEACHER_ACTIVE_BODY'),
        }),
      );
      yield delay(3000);
      if (!refreshingTokenInProgress) {
        yield put(authActions.refreshToken());
      }
      yield window.location.reload();
      break;
    case codes.TEACHER_SESSION_ACTIVE_CANNOT_LOGIN:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'SUPERTEACHER_MODAL_LOGOUT_ERROR_SESSION_TEACHER_ACTIVE_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'SUPERTEACHER_MODAL_LOGOUT_ERROR_SESSION_TEACHER_ACTIVE_BODY'),
        }),
      );
      break;
    case codes.EXPIRED_TOKEN:
      //jeśli token wygasł rozpocznij procedurę odświeżania
      //zapisz przekazaną akcję do akumulatora
      yield put(authActions.accumulateAction(failedAction));
      if (!refreshingTokenInProgress) {
        yield put(authActions.refreshToken());
      }
      break;

    case codes.MISSING_2FA:
      /* jeśli token nie jest podpisany 2FA usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(authActions.redirectToAuthService());
      break;
    case codes.BAD_TOKEN:
      /* jeśli token nie jest podpisany 2FA usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(authActions.redirectToAuthService());

      break;
    case codes.EXPIRED_REFRESH_TOKEN:
      /* jeśli token wygasł, wyświetl stosowną informację i przekieruj do serwisu uwierzytelniającego */
      //jeśli użytkownik jest superNauczycielem
      if (user.role === roles.SUPERTEACHER.id) {
        StorageManager.remove('TABS_CONFIG');
      }
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'EXPIRED_REFRESH_TOKEN_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'EXPIRED_REFRESH_TOKEN_REDIRECT_TO_CAS'),
        }),
      );
      yield delay(3000);
      yield put(authActions.redirectToAuthService());
      break;
    case codes.NO_AUTH_TOKEN:
    case codes.NOT_COMPLETE_AUTH_TOKEN:
    case codes.NO_AUNOT_VALID_AUTH_TOKENTH_TOKEN:
    case codes.NO_FACTOR_LIST_IN_HEADER:
    case codes.TOO_LOW_FACTOR_LEVEL:
    case codes.EMPTY_FACTOR_LIST_IN_HEADER:
    case codes.ERROR_NO_AUTHORIZATION:
    case codes.ERROR_WRONG_AUTHORIZATION:
    case codes.INVALID_TOKEN:
    case codes.ERROR_TOKEN_ERROR:
    case codes.ERROR_TOKEN_USER_MISSING_DB:
    case codes.ERROR_TOKEN_USER_ID_MISSING_DB:
    case codes.ERROR_TOKEN_USER_ID_MIN_DB:
    case codes.ERROR_TOKEN_USER_ID_MAX_DB:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_DB:
    case codes.ERROR_TOKEN_MISSING_DB:
    case codes.ERROR_TOKEN_INVALID_DB:
    case codes.URL_MALFORMED_DB:
    case codes.ERROR_TOKEN_ERROR_OCENY:
    case codes.ERROR_TOKEN_USER_MISSING_OCENY:
    case codes.ERROR_USER_MISSING_OCENY:
    case codes.ERROR_TOKEN_USER_ID_MISSING_OCENY:
    case codes.ERROR_TOKEN_USER_ID_MIN_OCENY:
    case codes.ERROR_TOKEN_USER_ID_MAX_OCENY:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_OCENY:
    case codes.URL_MALFORMED_OCENY:
    case codes.ERROR_USER_MISSING_PROTOKOLY:
    case codes.ERROR_TOKEN_USER_ID_MISSING_PROTOKOLY:
    case codes.ERROR_TOKEN_USER_ID_MIN_PROTOKOLY:
    case codes.ERROR_TOKEN_USER_ID_MAX_PROTOKOLY:
    case codes.ERROR_TOKEN_DATA_IS_NULL_PROTOKOLY:
    case codes.ERROR_PARSING_USER_FROM_TOKEN_PROTOKOLY:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_PROTOKOLY:
    case codes.ERROR_TOKEN_CANNOT_BE_PARSED_PROTOKOLY:
    case codes.URL_MALFORMED_PROTOKOLY:
    case codes.ERROR_TOKEN_MISSING_PRZEDMIOTY:
    case codes.ERROR_PARSING_USER_FROM_TOKEN_PRZEDMIOTY:
    case codes.ERROR_TOKEN_USER_ID_MISSING_PRZEDMIOTY:
    case codes.ERROR_TOKEN_USER_ID_MIN_PRZEDMIOTY:
    case codes.ERROR_TOKEN_USER_ID_MAX_PRZEDMIOTY:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_PRZEDMIOTY:
    case codes.URL_MALFORMED_PRZEDMIOTY:
    case codes.NO_SMS_API_TOKEN_SMS:
    case codes.URL_MALFORMED_SMS:
    case codes.ERROR_TOKEN_MISSING_STUDENCI:
    case codes.ERROR_PARSING_USER_FROM_TOKEN_STUDENCI:
    case codes.ERROR_TOKEN_USER_MISSING_STUDENCI:
    case codes.ERROR_TOKEN_USER_ID_MISSING_STUDENCI:
    case codes.ERROR_TOKEN_USER_ID_MIN_STUDENCI:
    case codes.ERROR_TOKEN_USER_ID_MAX_STUDENCI:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_STUDENCI:
    case codes.URL_MALFORMED_STUDENCI:
    case codes.ERROR_TOKEN_USER_MISSING_USER_CONFIG:
    case codes.ERROR_TOKEN_USER_ID_MISSING_USER_CONFIG:
    case codes.ERROR_TOKEN_USER_ID_MIN_USER_CONFIG:
    case codes.ERROR_TOKEN_USER_ID_MAX_USER_CONFIG:
    case codes.ERROR_TOKEN_USER_ID_NOT_NUMBER_USER_CONFIG:
    case codes.ERROR_TOKEN_MISSING_USER_CONFIG:
    case codes.ERROR_TOKEN_INVALID_USER_CONFIG:
    case codes.URL_MALFORMED_USER_CONFIG:
    case codes.TOKEN_USER_MISSING:
    case codes.TOKEN_USER_ID_MISSING:
    case codes.TOKEN_USER_ID_MIN:
    case codes.TOKEN_USER_ID_MAX:
    case codes.TOKEN_USER_ID_NOT_NUMBER:
    case codes.TOKEN_INVALID:
    case codes.WHOAMI_UNKNOWN_TOKEN:
    case codes.URL_MALFORMED_USERS:
    case codes.UNAUTHORIZED_USER_USERS:
      /* jeśli token nie jest podpisany 2FA usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'EXPIRED_REFRESH_TOKEN_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'EXPIRED_REFRESH_TOKEN_REDIRECT_TO_CAS'),
        }),
      );
      yield delay(3000);
      yield put(authActions.redirectToAuthService());
      break;
    case codes.FACTOR_CHANGED_TOKEN:
      /* jeśli faktor tokenu się zmienił usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(authActions.redirectToAuthService());

      break;
    case codes.MISSING_TOKEN:
      /* jeśli brakuje tokenu usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(authActions.redirectToAuthService());

      break;
    case codes.CANCELED_TOKEN:
      /* jeśli token został wycofany usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'EXPIRED_REFRESH_TOKEN_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'EXPIRED_REFRESH_TOKEN_REDIRECT_TO_CAS'),
        }),
      );
      yield delay(3000);
      yield put(authActions.redirectToAuthService());
      break;
    case codes.CANCELED_REFRESH_TOKEN:
      /* jeśli refresh token został wycofany usuń token i refresh token z localStorage i przekieruj
      do serwisu uwierzytelniającego */
      StorageManager.remove('id_token');
      StorageManager.remove('refresh_token');
      yield put(authActions.redirectToAuthService());
      break;
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_ACL_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_EMAIL_11:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_KP_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_11:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_12:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_MFA_13:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_DATABASE_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_OCENY_11:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PROTOKOLY_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_PRZEDMIOTY_11:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_SMS_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_STUDENCI_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USER_CONFIG_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USER_CONFIG_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USER_CONFIG_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USER_CONFIG_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_1:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_2:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_3:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_4:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_5:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_6:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_7:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_8:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_9:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_10:
    case codes.TYPICAL_ERROR_CODES_ALL_MICROSERVICES_USERS_11:
      /** jeśli któryś z powyższych błędów zostanie wywołany to wyświetl błędne żądanie (zły url, metoda itp) */
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'TYPICAL_ERROR_CODES_ALL_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'TYPICAL_ERROR_CODES_ALL_SUBTITLE'),
        }),
      );
      break;
    case codes.WRONG_DATA_KP_1:
    case codes.WRONG_DATA_KP_2:
    case codes.WRONG_DATA_KP_3:
    case codes.WRONG_DATA_KP_4:
    case codes.WRONG_DATA_KP_5:
    case codes.WRONG_DATA_KP_6:
    case codes.WRONG_DATA_KP_7:
    case codes.WRONG_DATA_KP_8:
    case codes.WRONG_DATA_KP_9:
    case codes.WRONG_DATA_KP_10:
    case codes.WRONG_DATA_KP_11:
    case codes.WRONG_DATA_KP_12:
    case codes.WRONG_DATA_KP_13:
    case codes.WRONG_DATA_KP_14:
    case codes.WRONG_DATA_KP_15:
    case codes.WRONG_DATA_KP_16:
    case codes.WRONG_DATA_KP_17:
    case codes.WRONG_DATA_KP_18:
    case codes.WRONG_DATA_KP_19:
    case codes.WRONG_DATA_KP_20:
    case codes.WRONG_DATA_KP_21:
    case codes.WRONG_DATA_KP_22:
    case codes.WRONG_DATA_KP_23:
    case codes.WRONG_DATA_KP_24:
    case codes.WRONG_DATA_KP_25:
    case codes.WRONG_DATA_KP_26:
    case codes.WRONG_DATA_KP_27:
    case codes.WRONG_DATA_KP_28:
    case codes.WRONG_DATA_KP_29:
    case codes.WRONG_DATA_KP_30:
    case codes.WRONG_DATA_KP_31:
    case codes.WRONG_DATA_KP_32:
    case codes.WRONG_DATA_KP_33:
    case codes.WRONG_DATA_KP_34:
    case codes.WRONG_DATA_KP_35:
    case codes.WRONG_DATA_KP_36:
    case codes.WRONG_DATA_KP_37:
    case codes.WRONG_DATA_KP_38:
    case codes.WRONG_DATA_KP_39:
    case codes.WRONG_DATA_KP_40:
    case codes.WRONG_DATA_DB_1:
    case codes.WRONG_DATA_DB_2:
    case codes.WRONG_DATA_DB_3:
    case codes.WRONG_DATA_DB_4:
    case codes.WRONG_DATA_DB_5:
    case codes.WRONG_DATA_DB_6:
    case codes.WRONG_DATA_DB_7:
    case codes.WRONG_DATA_DB_8:
    case codes.WRONG_DATA_DB_9:
    case codes.WRONG_DATA_DB_10:
    case codes.WRONG_DATA_DB_11:
    case codes.WRONG_DATA_DB_12:
    case codes.WRONG_DATA_DB_13:
    case codes.WRONG_DATA_DB_14:
    case codes.WRONG_DATA_DB_15:
    case codes.WRONG_DATA_DB_16:
    case codes.WRONG_DATA_DB_17:
    case codes.WRONG_DATA_DB_18:
    case codes.WRONG_DATA_DB_19:
    case codes.WRONG_DATA_DB_20:
    case codes.WRONG_DATA_DB_21:
    case codes.WRONG_DATA_DB_22:
    case codes.WRONG_DATA_DB_23:
    case codes.WRONG_DATA_DB_24:
    case codes.WRONG_DATA_DB_25:
    case codes.WRONG_DATA_DB_26:
    case codes.WRONG_DATA_DB_27:
    case codes.WRONG_DATA_DB_28:
    case codes.WRONG_DATA_DB_29:
    case codes.WRONG_DATA_DB_31:
    case codes.WRONG_DATA_DB_32:
    case codes.WRONG_DATA_DB_33:
    case codes.WRONG_DATA_DB_34:
    case codes.WRONG_DATA_DB_35:
    case codes.WRONG_DATA_DB_36:
    case codes.WRONG_DATA_DB_39:
    case codes.WRONG_DATA_DB_40:
    case codes.WRONG_DATA_DB_41:
    case codes.WRONG_DATA_DB_42:
    case codes.WRONG_DATA_DB_43:
    case codes.WRONG_DATA_DB_45:
    case codes.WRONG_DATA_OCENY_1:
    case codes.WRONG_DATA_OCENY_2:
    case codes.WRONG_DATA_OCENY_3:
    case codes.WRONG_DATA_OCENY_4:
    case codes.WRONG_DATA_OCENY_5:
    case codes.WRONG_DATA_OCENY_6:
    case codes.WRONG_DATA_OCENY_7:
    case codes.WRONG_DATA_OCENY_8:
    case codes.WRONG_DATA_OCENY_9:
    case codes.WRONG_DATA_OCENY_10:
    case codes.WRONG_DATA_OCENY_11:
    case codes.WRONG_DATA_OCENY_12:
    case codes.WRONG_DATA_OCENY_13:
    case codes.WRONG_DATA_OCENY_14:
    case codes.WRONG_DATA_OCENY_15:
    case codes.WRONG_DATA_OCENY_16:
    case codes.WRONG_DATA_OCENY_17:
    case codes.WRONG_DATA_OCENY_18:
    case codes.WRONG_DATA_OCENY_19:
    case codes.WRONG_DATA_OCENY_20:
    case codes.WRONG_DATA_OCENY_21:
    case codes.WRONG_DATA_OCENY_22:
    case codes.WRONG_DATA_OCENY_23:
    case codes.WRONG_DATA_OCENY_24:
    case codes.WRONG_DATA_OCENY_25:
    case codes.WRONG_DATA_OCENY_26:
    case codes.WRONG_DATA_PROTOKOLY_1:
    case codes.WRONG_DATA_PROTOKOLY_2:
    case codes.WRONG_DATA_PROTOKOLY_3:
    case codes.WRONG_DATA_PROTOKOLY_4:
    case codes.WRONG_DATA_PROTOKOLY_5:
    case codes.WRONG_DATA_PROTOKOLY_6:
    case codes.WRONG_DATA_PROTOKOLY_7:
    case codes.WRONG_DATA_PROTOKOLY_8:
    case codes.WRONG_DATA_PROTOKOLY_9:
    case codes.WRONG_DATA_PROTOKOLY_10:
    case codes.WRONG_DATA_PROTOKOLY_11:
    case codes.WRONG_DATA_PROTOKOLY_12:
    case codes.WRONG_DATA_PROTOKOLY_13:
    case codes.WRONG_DATA_PROTOKOLY_14:
    case codes.WRONG_DATA_PROTOKOLY_15:
    case codes.WRONG_DATA_PROTOKOLY_16:
    case codes.WRONG_DATA_PRZEDMIOTY_1:
    case codes.WRONG_DATA_PRZEDMIOTY_2:
    case codes.WRONG_DATA_PRZEDMIOTY_3:
    case codes.WRONG_DATA_PRZEDMIOTY_4:
    case codes.WRONG_DATA_PRZEDMIOTY_5:
    case codes.WRONG_DATA_PRZEDMIOTY_6:
    case codes.WRONG_DATA_PRZEDMIOTY_7:
    case codes.WRONG_DATA_PRZEDMIOTY_8:
    case codes.WRONG_DATA_PRZEDMIOTY_9:
    case codes.WRONG_DATA_PRZEDMIOTY_10:
    case codes.WRONG_DATA_PRZEDMIOTY_11:
    case codes.WRONG_DATA_PRZEDMIOTY_12:
    case codes.WRONG_DATA_PRZEDMIOTY_13:
    case codes.WRONG_DATA_PRZEDMIOTY_14:
    case codes.WRONG_DATA_PRZEDMIOTY_15:
    case codes.WRONG_DATA_PRZEDMIOTY_16:
    case codes.WRONG_DATA_PRZEDMIOTY_17:
    case codes.WRONG_DATA_SMS_1:
    case codes.WRONG_DATA_SMS_2:
    case codes.WRONG_DATA_SMS_3:
    case codes.WRONG_DATA_SMS_4:
    case codes.WRONG_DATA_STUDENCI_1:
    case codes.WRONG_DATA_STUDENCI_2:
    case codes.WRONG_DATA_STUDENCI_3:
    case codes.WRONG_DATA_STUDENCI_4:
    case codes.WRONG_DATA_STUDENCI_5:
    case codes.WRONG_DATA_STUDENCI_6:
    case codes.WRONG_DATA_STUDENCI_7:
    case codes.WRONG_DATA_STUDENCI_8:
    case codes.WRONG_DATA_STUDENCI_9:
    case codes.WRONG_DATA_STUDENCI_10:
    case codes.WRONG_DATA_STUDENCI_11:
    case codes.WRONG_DATA_STUDENCI_12:
    case codes.WRONG_DATA_STUDENCI_13:
    case codes.WRONG_DATA_STUDENCI_14:
    case codes.WRONG_DATA_STUDENCI_15:
    case codes.WRONG_DATA_STUDENCI_16:
    case codes.WRONG_DATA_STUDENCI_17:
    case codes.WRONG_DATA_STUDENCI_18:
    case codes.WRONG_DATA_STUDENCI_19:
    case codes.WRONG_DATA_STUDENCI_20:
    case codes.WRONG_DATA_STUDENCI_21:
    case codes.WRONG_DATA_STUDENCI_22:
    case codes.WRONG_DATA_STUDENCI_23:
    case codes.WRONG_DATA_USER_CONFIG_1:
    case codes.WRONG_DATA_USER_CONFIG_2:
    case codes.WRONG_DATA_USER_CONFIG_3:
    case codes.WRONG_DATA_USER_CONFIG_4:
    case codes.WRONG_DATA_USER_CONFIG_5:
    case codes.WRONG_DATA_USER_CONFIG_6:
    case codes.WRONG_DATA_USER_CONFIG_7:
    case codes.WRONG_DATA_USER_CONFIG_8:
    case codes.WRONG_DATA_USER_CONFIG_9:
    case codes.WRONG_DATA_USER_CONFIG_10:
    case codes.WRONG_DATA_USER_CONFIG_11:
    case codes.WRONG_DATA_USER_CONFIG_12:
    case codes.WRONG_DATA_USER_CONFIG_13:
    case codes.WRONG_DATA_USER_CONFIG_14:
    case codes.WRONG_DATA_USER_CONFIG_15:
    case codes.WRONG_DATA_USER_CONFIG_16:
    case codes.WRONG_DATA_USER_CONFIG_17:
      /** W momencie wyświetlenia powyższych kodów błędów (złe dane, brak parametru, brak kolumny itd) */
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'WRONG_DATA_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'WRONG_DATA_SUBTITLE'),
        }),
      );
      break;
    case codes.WRONG_DATA_DB_30:
    case codes.WRONG_DATA_DB_37:
    case codes.WRONG_DATA_DB_38:
    case codes.WRONG_DATA_DB_46:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'WRONG_DATA_TITLE',
          bodyTranslationKey: createBodyFromError(error),
        }),
      );
      break;
    case codes.WRONG_DATA_DB_44:
      /** W momencie otrzymania tych kodów błędów nie podejmuj żadnej akcji */
      break;
    case codes.MAINTENANCE_MODE:
      // jeśli api zwróciło kod o maintenance mode to ustaw jego wartość na true
      const maintenanceList = error?.message ? error.message : [];
      yield put(setMaintenanceMode(maintenanceList));
      break;
    //   /* Jesli użytkownik nie ma uprawnień do danych zasobów, nie podejmuj żadnej akcji */
    case codes.ERROR_PROTOCOL_GENERATION_NO_SUM_CONTROL:
      //wyświetl informację użytkownikowi
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'ERROR_PROTOCOL_GENERATION_NO_SUM_CONTROL_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'ERROR_PROTOCOL_GENERATION_NO_SUM_CONTROL_BODY'),
        }),
      );
      break;
    default:
      yield put(
        showNotification({
          notifType: 'error' as NotificationTypes,
          titleTranslationKey: 'UNKNOWED_ERROR_TITLE',
          bodyTranslationKey: createBodyFromError(error, 'UNKNOWED_ERROR_BODY'),
        }),
      );
  }
}

export function* logoutWatcher(): Generator {
  yield takeEvery(authActionTypes.LOGOUT, logout);
}
/**
 * wylogowanie z aplikacji
 *
 * @export
 * @return {*}  {Generator}
 */
export function* logout(): Generator {
  /* usuń cała zawartość storage managera */
  try {
    yield call(Auth.logout);
  } catch (error) {
    StorageManager.clearTokens();
  }
  StorageManager.clearTokens();
  /* usuń informacje o użytkowniku ze store */
  yield put(authActions.resetUser());
  /* wykonaj akcję przekierowania na stronę wylogowania z CAS */
  yield put(authActions.redirectToCASLogout());
}

export function* redirectToCasLogoutWatcher(): Generator {
  yield takeEvery(authActionTypes.REDIRECT_TO_CAS_LOGOUT, redirectToCasLogout);
}
/**
 * przerkierowanie do strony wylogowania z CAS
 *
 * @export
 * @return {*}  {Generator}
 */
export function* redirectToCasLogout(): Generator {
  /* przekieruj do strony wylogowania z CAS */
  yield window.location.replace(casLogoutUrl);
}
export function* redirectToAuthServiceWatcher(): Generator {
  yield takeEvery(authActionTypes.REDIRECT_TO_AUTH_SERVICE, redirectToAuthService);
}
/**
 * przekierowanie do seriwsu uwierzytelniającego0
 *
 * @export
 * @return {*}  {Generator}
 */
export function* redirectToAuthService(): Generator {
  /* przekieruj do serwisu uwierzytelniającego */
  yield window.location.replace(`${authServiceUrl}/?return_url=${window.location.origin}`);
}

export default function* rootSaga(): Generator {
  yield all([
    fork(whoIAmWatcher),
    fork(checkTokenWatcher),
    fork(executeActionCodesWatcher),
    fork(executeErrorCodesWatcher),
    fork(redirectToAuthServiceWatcher),
    fork(logoutWatcher),
    fork(redirectToCasLogoutWatcher),
    fork(refreshTokenWatcher),
    fork(initAutoRefreshWatcher),
    fork(whoIAmBackgroundWatcher),
    fork(storeTokenWatcher),
    fork(executeActionsFromActionAccumulator),
  ]);
}
